import type {
  RouteLocationNormalized,
  RouteRecordNormalized,
} from 'vue-router';
import type { App, Plugin } from 'vue';
import { ImagePrefixEnum } from '/@/enums/appEnum';
import { unref } from 'vue';
import { isObject } from '/@/utils/is';
import dayjs from 'dayjs';
export const noop = () => {};

/**
 * @description:  Set ui mount node
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  return (node?.parentNode as HTMLElement) ?? document.body;
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = '';
  for (const key in obj) {
    parameters += key + '=' + encodeURIComponent(obj[key]) + '&';
  }
  parameters = parameters.replace(/&$/, '');
  return /\?$/.test(baseUrl)
    ? baseUrl + parameters
    : baseUrl.replace(/\/?$/, '?') + parameters;
}

export function deepMerge<T = any>(src: any = {}, target: any = {}): T {
  let key: string;
  for (key in target) {
    src[key] = isObject(src[key])
      ? deepMerge(src[key], target[key])
      : (src[key] = target[key]);
  }
  return src;
}

export function openWindow(
  url: string,
  opt?: {
    target?: TargetContext | string;
    noopener?: boolean;
    noreferrer?: boolean;
  }
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {};
  const feature: string[] = [];

  noopener && feature.push('noopener=yes');
  noreferrer && feature.push('noreferrer=yes');

  window.open(url, target, feature.join(','));
}

// dynamic use hook props
export function getDynamicProps<T, U>(props: T): Partial<U> {
  const ret: Recordable = {};

  Object.keys(props).map((key) => {
    ret[key] = unref((props as Recordable)[key]);
  });

  return ret as Partial<U>;
}

export function getRawRoute(
  route: RouteLocationNormalized
): RouteLocationNormalized {
  if (!route) return route;
  const { matched, ...opt } = route;
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  };
}

export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any;
  comp.install = (app: App) => {
    app.component(comp.name || comp.displayName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as T & Plugin;
};

export const handleImgPath = (path: string) => {
  return path && path.indexOf('http') > -1
    ? path
    : ImagePrefixEnum.aliyun + path;
};
/**
 * @description：格式化地址
 * @author: zhaojs
 * @date: 2024-02-20 18:03:26
 */
export const formatURL = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) {
    return path;
  }
  if (path.startsWith('jap')) {
    return ImagePrefixEnum.japOSS + path;
  }
  return ImagePrefixEnum.aliyun + path;
};

/**
 * @description：格式化数字千分位
 */

export const formatNumThousands = (num: number | string) => {
  return (num || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * 处理搜索条件中时间格式
 * time：时间数组
 * type: 0：开始时间 1：结束时间
 * */
export const dateFormat = (time, type: number) => {
  if (time?.length) {
    const str = ['YYYY-MM-DD 00:00:00', 'YYYY-MM-DD 23:59:59'];
    return dayjs(time[type]).format(str[type]);
  } else {
    return '';
  }
};

export const formatSeconds = (seconds) => {
  const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${hours}时${minutes}分${secs}秒`;
};
